import { useState } from 'react'
import {
  Box,
  Button,
  Heading,
  HStack,
  NativeSelect,
  Spinner,
  Text,
  VStack,
  Badge,
} from '@chakra-ui/react';
import { FiPlus } from 'react-icons/fi'
import { useNavigate } from 'react-router-dom'
import { useLobbyDayEvents } from '../hooks/useLobbyDay'
import { EventFormModal } from '../components/lobbyDay/EventFormModal'
import { ResponsiveTable, FilterBar, type ResponsiveColumn } from '../components/shared'
import type { LobbyDayEventSummaryDto, LobbyDayStatus } from '../types/lobbyDay'

const STATUS_COLORS: Record<LobbyDayStatus, string> = {
  Draft: 'gray',
  Active: 'green',
  Completed: 'blue',
  Archived: 'purple',
}

export function LobbyDayListPage() {
  const navigate = useNavigate()
  const [status, setStatus] = useState('')
  const [isCreateOpen, setCreateOpen] = useState(false)
  const { data: events, isLoading, error } = useLobbyDayEvents(status || undefined)

  const columns: ResponsiveColumn<LobbyDayEventSummaryDto>[] = [
    {
      header: 'Name',
      mobileRole: 'title',
      cell: (ev) => ev.name,
    },
    {
      header: 'Date',
      mobileRole: 'meta',
      cell: (ev) => `${ev.startDate}${ev.endDate ? ` — ${ev.endDate}` : ''}`,
    },
    {
      header: 'Location',
      mobileRole: 'meta',
      cell: (ev) => ev.location ?? '—',
    },
    {
      header: 'Status',
      mobileRole: 'badge',
      cell: (ev) => <Badge colorPalette={STATUS_COLORS[ev.status]}>{ev.status}</Badge>,
    },
    {
      header: 'Attendees',
      mobileRole: 'meta',
      isNumeric: true,
      cell: (ev) => ev.attendeeCount,
    },
    {
      header: 'Meetings',
      mobileRole: 'meta',
      isNumeric: true,
      cell: (ev) => ev.meetingCount,
    },
  ]

  return (
    <Box>
      <HStack justify="space-between" flexWrap="wrap" gap={3} mb={6}>
        <Heading size="lg">Lobby Day Events</Heading>
        <Button colorPalette="brand" size="sm" onClick={() => setCreateOpen(true)}><FiPlus />New Event
                  </Button>
      </HStack>

      <FilterBar filterCount={status !== '' ? 1 : 0}>
        <NativeSelect.Root size="sm">
          <NativeSelect.Field
            w="160px"
            value={status}
            onChange={(e) => setStatus(e.target.value)}>
            <option value="">All statuses</option>
            <option value="Draft">Draft</option>
            <option value="Active">Active</option>
            <option value="Completed">Completed</option>
            <option value="Archived">Archived</option>
          </NativeSelect.Field>
          <NativeSelect.Indicator />
        </NativeSelect.Root>
      </FilterBar>

      {isLoading && (
        <VStack py={12}><Spinner size="lg" color="accent" /></VStack>
      )}
      {!!error && (
        <Text color="red.500">Failed to load events.</Text>
      )}

      {events && (
        <ResponsiveTable
          columns={columns}
          rows={events}
          keyFn={(ev) => ev.id}
          onRowClick={(ev) => navigate(`/dashboard/lobby-day/events/${ev.id}`)}
          emptyMessage="No lobby day events yet. Create the first one."
        />
      )}

      <EventFormModal isOpen={isCreateOpen} onClose={() => setCreateOpen(false)} />
    </Box>
  );
}
